import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Breadcrumb, BreadcrumbItem, Tooltip } from "flowbite-react";
import { HiDownload, HiHome, HiInformationCircle } from "react-icons/hi";
import NavbarSidebarLayout from "../../layouts/navbar-sidebar";
import { useAuth } from "../../contexts/AuthContext";
import {
  ExportFilterBuilder,
  type ExportDraft,
} from "../../components/exports/ExportFilterBuilder";
import { CrossSellingNotice } from "../../components/exports/CrossSellingNotice";
import ExportHistoryTable from "../../components/exports/ExportHistoryTable";
import {
  createExport,
  getExportDownload,
  getExportWarningsFile,
  listExportJobs,
  loadAllMissingWorkerIds,
  loadExportOptions,
  previewExport,
  retryExport,
} from "../../services/exports";
import type {
  ExportFilters,
  ExportHistoryCursor,
  ExportJob,
  ExportOption,
  ExportOptionKind,
  ExportPreview,
} from "../../types/exports";
import {
  DEFAULT_EXPORT_PERIOD,
  getBerlinToday,
  resolveExportPeriod,
  type ExportDateRange,
} from "../../utils/exportDateRanges";

const optionKinds: ExportOptionKind[] = [
  "providers",
  "contract_types",
  "teams",
  "workers",
];
const initialOptions = (): Record<ExportOptionKind, ExportOption[]> => ({
  providers: [],
  contract_types: [],
  teams: [],
  workers: [],
});
const initialLoading = (): Record<ExportOptionKind, boolean> => ({
  providers: true,
  contract_types: false,
  teams: true,
  workers: true,
});
function getErrorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Die Anfrage ist fehlgeschlagen. Bitte erneut versuchen.";
}

function filtersFor(draft: ExportDraft, range: ExportDateRange): ExportFilters {
  return {
    date_from: range.dateFrom,
    date_to_exclusive: range.dateToExclusive,
    provider_ids: [...draft.providerIds].sort((a, b) => a - b),
    contract_type_ids: [...draft.contractTypeIds].sort((a, b) => a - b),
    team_ids: [...draft.teamIds].sort((a, b) => a - b),
    worker_ids: [...draft.workerIds].sort((a, b) => a - b),
  };
}

export default function ExportPage() {
  const { user } = useAuth();
  return user ? <ExportPageContent key={user.id} /> : null;
}

function ExportPageContent() {
  const [draft, setDraft] = useState<ExportDraft>(() => ({
    period: DEFAULT_EXPORT_PERIOD,
    providerIds: [],
    contractTypeIds: [],
    teamIds: [],
    workerIds: [],
  }));
  const [options, setOptions] = useState(initialOptions);
  const [optionLoading, setOptionLoading] = useState(initialLoading);
  const [optionErrors, setOptionErrors] = useState<
    Partial<Record<ExportOptionKind, string>>
  >({});
  const [optionRefresh, setOptionRefresh] = useState(0);
  const [preview, setPreview] = useState<ExportPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [addingWorkers, setAddingWorkers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [acceptedFingerprint, setAcceptedFingerprint] = useState<string | null>(
    null
  );
  const [prunedTypeNotice, setPrunedTypeNotice] = useState<string | null>(null);
  const [today, setToday] = useState(getBerlinToday);
  const [jobs, setJobs] = useState<ExportJob[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [cursors, setCursors] = useState<(ExportHistoryCursor | null)[]>([
    null,
  ]);
  const [page, setPage] = useState(0);
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState<string | null>(null);
  const [downloadingWarningsJobId, setDownloadingWarningsJobId] = useState<
    string | null
  >(null);
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const previewVersion = useRef(0);
  const optionVersion = useRef<Record<ExportOptionKind, number>>({
    providers: 0,
    contract_types: 0,
    teams: 0,
    workers: 0,
  });
  const filterFingerprint = useRef("");
  const submission = useRef<{ fingerprint: string; key: string } | null>(null);
  const submitLock = useRef(false);
  const retryLock = useRef(false);
  const retryKeys = useRef<Record<string, string>>({});
  const pollDelay = useRef(15_000);
  const lastHistoryProgress = useRef<string | null>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  useEffect(() => {
    const refreshToday = () => setToday(getBerlinToday());
    const timer = window.setInterval(refreshToday, 60_000);
    document.addEventListener("visibilitychange", refreshToday);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshToday);
    };
  }, []);

  const { range, dateError } = useMemo(() => {
    try {
      return {
        range: resolveExportPeriod(draft.period, today),
        dateError: null,
      };
    } catch (error) {
      return { range: null, dateError: getErrorMessage(error) };
    }
  }, [draft.period, today]);

  const filters = useMemo(
    () => (range ? filtersFor(draft, range) : null),
    [draft, range]
  );
  const fingerprint = filters ? JSON.stringify(filters) : "";
  filterFingerprint.current = fingerprint;

  useEffect(() => {
    if (submission.current && submission.current.fingerprint !== fingerprint)
      submission.current = null;
    if (acceptedFingerprint && acceptedFingerprint !== fingerprint)
      setAcceptedFingerprint(null);
  }, [fingerprint, acceptedFingerprint]);

  // Options are restricted and paginated by the export RPC. All pages are loaded
  // so searching never silently omits a worker or a historical inactive label.
  const loadKind = useCallback(
    async (kind: ExportOptionKind, providerIds?: number[]) => {
      const version = ++optionVersion.current[kind];
      setOptionLoading((previous) => ({ ...previous, [kind]: true }));
      setOptionErrors((previous) => ({ ...previous, [kind]: undefined }));
      try {
        const items = await loadExportOptions(kind, providerIds);
        if (optionVersion.current[kind] !== version) return;
        setOptions((previous) => ({ ...previous, [kind]: items }));
        if (kind === "contract_types") {
          const ids = new Set(items.map((item) => item.id));
          const removed = draftRef.current.contractTypeIds.filter(
            (id) => !ids.has(id)
          ).length;
          if (removed)
            setPrunedTypeNotice(
              `${removed} nicht mehr passende Vertragsarten wurden aus der Auswahl entfernt.`
            );
          setDraft((previous) => {
            const retained = previous.contractTypeIds.filter((id) =>
              ids.has(id)
            );
            return retained.length === previous.contractTypeIds.length
              ? previous
              : { ...previous, contractTypeIds: retained };
          });
        }
      } catch (error) {
        if (optionVersion.current[kind] === version) {
          setOptions((previous) => ({ ...previous, [kind]: [] }));
          setOptionErrors((previous) => ({
            ...previous,
            [kind]: getErrorMessage(error),
          }));
        }
      } finally {
        if (optionVersion.current[kind] === version)
          setOptionLoading((previous) => ({ ...previous, [kind]: false }));
      }
    },
    []
  );

  useEffect(() => {
    void Promise.all([
      loadKind("providers"),
      loadKind("teams"),
      loadKind("workers"),
    ]);
  }, [loadKind, optionRefresh]);

  const providerFingerprint = draft.providerIds
    .slice()
    .sort((a, b) => a - b)
    .join(",");
  useEffect(() => {
    if (!providerFingerprint) {
      ++optionVersion.current.contract_types;
      setOptions((previous) => ({ ...previous, contract_types: [] }));
      setOptionLoading((previous) => ({ ...previous, contract_types: false }));
      return;
    }
    void loadKind("contract_types", providerFingerprint.split(",").map(Number));
  }, [loadKind, providerFingerprint, optionRefresh]);

  const refreshPreview = useCallback(
    async (requestedFilters: ExportFilters, version: number) => {
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const result = await previewExport(requestedFilters);
        if (previewVersion.current === version) setPreview(result);
      } catch (error) {
        if (previewVersion.current === version)
          setPreviewError(getErrorMessage(error));
      } finally {
        if (previewVersion.current === version) setPreviewLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    const version = ++previewVersion.current;
    setPreview(null);
    setPreviewError(null);
    setPreviewLoading(false);
    if (
      !filters ||
      !filters.team_ids.length ||
      !filters.provider_ids.length ||
      optionErrors.providers ||
      optionErrors.contract_types ||
      optionLoading.contract_types
    )
      return;
    const timer = window.setTimeout(
      () => void refreshPreview(filters, version),
      350
    );
    return () => window.clearTimeout(timer);
  }, [
    fingerprint,
    filters,
    refreshPreview,
    optionErrors.providers,
    optionErrors.contract_types,
    optionLoading.contract_types,
  ]);

  const handleAddMissing = useCallback(async () => {
    if (!filters || !preview || addingWorkers) return;
    const freshToday = getBerlinToday();
    if (freshToday !== today) {
      setToday(freshToday);
      setPreviewError(
        "Der Kalendertag hat gewechselt. Bitte die aktualisierte Vorschau prüfen."
      );
      return;
    }
    const expectedFingerprint = fingerprint;
    const expectedCount = preview.missing_worker_count;
    setAddingWorkers(true);
    setPreviewError(null);
    try {
      const ids = await loadAllMissingWorkerIds(
        filters,
        expectedCount,
        () => filterFingerprint.current === expectedFingerprint
      );
      if (filterFingerprint.current === expectedFingerprint) {
        setDraft((previous) => ({
          ...previous,
          workerIds: [...new Set([...previous.workerIds, ...ids])],
        }));
      }
    } catch (error) {
      if (filterFingerprint.current === expectedFingerprint)
        setPreviewError(getErrorMessage(error));
    } finally {
      setAddingWorkers(false);
    }
  }, [filters, preview, addingWorkers, fingerprint, today]);

  const refreshHistory = useCallback(
    () => setHistoryRefresh((value) => value + 1),
    []
  );
  const historyCursor = cursors[page] ?? null;
  useEffect(() => {
    let current = true;
    setHistoryLoading(true);
    void listExportJobs(historyCursor)
      .then((result) => {
        if (!current) return;
        setJobs(result.jobs);
        setHistoryHasMore(result.hasMore);
        const progress = result.jobs
          .map((job) => `${job.id}:${job.status}:${job.processed_rows}`)
          .join("|");
        pollDelay.current =
          progress === lastHistoryProgress.current
            ? Math.min(pollDelay.current * 2, 60_000)
            : 15_000;
        lastHistoryProgress.current = progress;
        setCursors((previous) => {
          const next = previous.slice(0, page + 1);
          if (result.nextCursor) next.push(result.nextCursor);
          return next;
        });
        setHistoryError(null);
      })
      .catch((error) => {
        if (current) {
          setJobs([]);
          setHistoryHasMore(false);
          setHistoryError(getErrorMessage(error));
          pollDelay.current = Math.min(pollDelay.current * 2, 60_000);
        }
      })
      .finally(() => {
        if (current) setHistoryLoading(false);
      });
    return () => {
      current = false;
    };
  }, [page, historyCursor, historyRefresh]);

  const hasActiveJobs = jobs.some(
    (job) => job.status === "pending" || job.status === "running"
  );
  useEffect(() => {
    if (!hasActiveJobs) return;
    let timer: number | undefined;
    const schedule = () => {
      if (!document.hidden)
        timer = window.setTimeout(refreshHistory, pollDelay.current);
    };
    const onVisibility = () => {
      if (timer !== undefined) window.clearTimeout(timer);
      if (!document.hidden) {
        pollDelay.current = 15_000;
        lastHistoryProgress.current = null;
        refreshHistory();
      }
    };
    schedule();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [hasActiveJobs, jobs, refreshHistory]);

  const handleSubmit = async () => {
    if (
      !filters ||
      acceptedFingerprint === fingerprint ||
      submitLock.current ||
      optionKinds.some((kind) => optionLoading[kind] || !!optionErrors[kind])
    )
      return;
    if (!filters.provider_ids.length) {
      setSubmitError("Bitte mindestens einen Vertragsanbieter auswählen.");
      return;
    }
    const freshToday = getBerlinToday();
    if (freshToday !== today) {
      setToday(freshToday);
      setSubmitError(
        "Der Kalendertag hat gewechselt. Bitte den aktualisierten Zeitraum prüfen und erneut starten."
      );
      return;
    }
    submitLock.current = true;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    const key =
      submission.current?.fingerprint === fingerprint
        ? submission.current.key
        : crypto.randomUUID();
    const submittedFingerprint = fingerprint;
    submission.current = { fingerprint, key };
    try {
      await createExport(filters, key);
      if (filterFingerprint.current === submittedFingerprint) {
        setAcceptedFingerprint(submittedFingerprint);
        setSubmitSuccess(
          "Export angefordert. Die Datei erscheint nach der Hintergrundverarbeitung in der Liste."
        );
      }
      setPage(0);
      setCursors([null]);
      refreshHistory();
    } catch (error) {
      if (filterFingerprint.current === submittedFingerprint)
        setSubmitError(getErrorMessage(error));
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  const handleDownload = async (job: ExportJob) => {
    if (downloadingJobId) return;
    setDownloadingJobId(job.id);
    setActionError(null);
    try {
      const file = await getExportDownload(job.id);
      const link = document.createElement("a");
      link.href = file.url;
      link.download = file.filename;
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setActionError(getErrorMessage(error));
    } finally {
      setDownloadingJobId(null);
    }
  };

  const handleDownloadWarnings = async (job: ExportJob) => {
    if (downloadingWarningsJobId) return;
    setDownloadingWarningsJobId(job.id);
    setActionError(null);
    try {
      const file = await getExportWarningsFile(job);
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Give the browser a moment to start the download before revoking.
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (error) {
      setActionError(getErrorMessage(error));
    } finally {
      setDownloadingWarningsJobId(null);
    }
  };

  const handleRetry = async (job: ExportJob) => {
    if (retryLock.current) return;
    retryLock.current = true;
    setRetryingJobId(job.id);
    setActionError(null);
    try {
      const key = retryKeys.current[job.id] ?? crypto.randomUUID();
      retryKeys.current[job.id] = key;
      await retryExport(job.id, key);
      setPage(0);
      setCursors([null]);
      refreshHistory();
    } catch (error) {
      setActionError(getErrorMessage(error));
    } finally {
      retryLock.current = false;
      setRetryingJobId(null);
    }
  };

  return (
    <NavbarSidebarLayout pageTitle="Vertragsexport">
      <div className="p-4">
        <header className="border-b border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 lg:mt-1.5">
          <Breadcrumb className="mb-4">
            <BreadcrumbItem href="/">
              <div className="flex items-center gap-x-3">
                <HiHome className="text-xl" />
                <span className="dark:text-white">Startseite</span>
              </div>
            </BreadcrumbItem>
            <BreadcrumbItem>Export</BreadcrumbItem>
          </Breadcrumb>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
            Vertragsexport
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Wählen Sie Zeitraum, Anbieter und optional Vertragsarten, Teams oder
            Mitarbeitende. Der CSV-Export läuft im Hintergrund.
          </p>
        </header>

        <div className="mt-4 space-y-4">
          <ExportFilterBuilder
            draft={draft}
            onChange={(next) => {
              if (next.providerIds.join(",") !== draft.providerIds.join(",")) {
                setOptionLoading((previous) => ({
                  ...previous,
                  contract_types: true,
                }));
                const removed =
                  draft.contractTypeIds.length - next.contractTypeIds.length;
                setPrunedTypeNotice(
                  removed > 0
                    ? `${removed} nicht mehr passende Vertragsarten wurden aus der Auswahl entfernt.`
                    : null
                );
              }
              setDraft(next);
              setSubmitError(null);
              setSubmitSuccess(null);
            }}
            options={options}
            loading={optionLoading}
            errors={optionErrors}
            range={range}
            dateError={dateError}
            today={today}
            actions={
              <>
                <Tooltip
                  content="Der Export enthält auch Kundennotizen. Für Postleitzahlen, Vertrags- und Zählernummern die CSV-Spalten beim Import als Text festlegen, sonst erkennen Tabellenprogramme sie möglicherweise als Zahlen. Auffällige Textanfänge werden beim Export für Tabellenprogramme geschützt."
                  className="max-w-xs text-xs"
                >
                  <button
                    type="button"
                    className="rounded p-1 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:text-gray-300"
                    aria-label="Hinweise zur CSV-Datei: Enthält Kundennotizen. Postleitzahlen, Vertrags- und Zählernummern beim Import als Text festlegen."
                  >
                    <HiInformationCircle
                      className="h-5 w-5"
                      aria-hidden="true"
                    />
                  </button>
                </Tooltip>
                <button
                  type="button"
                  onClick={() => void handleSubmit()}
                  disabled={
                    submitting ||
                    acceptedFingerprint === fingerprint ||
                    !range ||
                    !draft.providerIds.length ||
                    optionKinds.some(
                      (kind) => optionLoading[kind] || !!optionErrors[kind]
                    )
                  }
                  className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-blue-700 px-4 text-sm font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <HiDownload className="h-4 w-4" aria-hidden="true" />
                  {submitting
                    ? "Export wird angefordert …"
                    : acceptedFingerprint === fingerprint
                    ? "Export bereits angefordert"
                    : "CSV-Export erstellen"}
                </button>
              </>
            }
          />
          {submitError && (
            <p
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-gray-800 dark:text-red-300"
            >
              {submitError}
            </p>
          )}
          {submitSuccess && (
            <p
              role="status"
              className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700 dark:border-green-900 dark:bg-gray-800 dark:text-green-300"
            >
              {submitSuccess}
            </p>
          )}
          {prunedTypeNotice && (
            <p
              role="status"
              className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800 dark:bg-gray-800 dark:text-blue-200"
            >
              {prunedTypeNotice}
            </p>
          )}
          {Object.values(optionErrors).some(Boolean) && (
            <div
              role="alert"
              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-gray-800 dark:text-red-300"
            >
              <span>
                Filteroptionen konnten nicht vollständig geladen werden. Ein
                Export ist erst nach erfolgreichem Laden möglich.
              </span>
              <button
                type="button"
                onClick={() => setOptionRefresh((value) => value + 1)}
                className="rounded-lg border border-red-300 px-3 py-1 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Erneut laden
              </button>
            </div>
          )}
          {draft.teamIds.length > 0 && (
            <CrossSellingNotice
              preview={preview}
              loading={previewLoading}
              adding={addingWorkers}
              error={previewError}
              onAddAll={() => void handleAddMissing()}
              onRefresh={() => {
                const freshToday = getBerlinToday();
                if (freshToday !== today) {
                  setToday(freshToday);
                  return;
                }
                if (filters?.provider_ids.length)
                  void refreshPreview(filters, ++previewVersion.current);
              }}
            />
          )}
          {historyError && (
            <p
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-gray-800 dark:text-red-300"
            >
              {historyError}
            </p>
          )}
          {actionError && (
            <p
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-gray-800 dark:text-red-300"
            >
              {actionError}
            </p>
          )}
          <ExportHistoryTable
            jobs={jobs}
            loading={historyLoading}
            error={historyError}
            page={page + 1}
            hasMore={historyHasMore}
            onPrevious={() => setPage((value) => Math.max(0, value - 1))}
            onNext={() => {
              if (historyHasMore && cursors[page + 1])
                setPage((value) => value + 1);
            }}
            onRefresh={refreshHistory}
            onDownload={(job) => void handleDownload(job)}
            onRetry={(job) => void handleRetry(job)}
            onDownloadWarnings={(job) => void handleDownloadWarnings(job)}
            downloadingJobId={downloadingJobId}
            downloadingWarningsJobId={downloadingWarningsJobId}
            retryingJobId={retryingJobId}
          />
        </div>
      </div>
    </NavbarSidebarLayout>
  );
}
